/**
 * Wraps a canvas into a single-page A4 PDF (no dependencies): the canvas is
 * encoded as JPEG and embedded full-bleed on a 595.28 x 841.89pt page.
 * Render the canvas at an A4 aspect (e.g. 1240x1754 = A4 @150dpi).
 */
export function CanvasToA4Pdf(canvas: HTMLCanvasElement): Blob {
	const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
	const binary = atob(dataUrl.split(",")[1]);
	const jpeg = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) jpeg[i] = binary.charCodeAt(i);

	const encoder = new TextEncoder();
	const chunks: Uint8Array[] = [];
	const offsets: number[] = [];
	let position = 0;

	const push = (text: string | Uint8Array) => {
		const bytes = typeof text === "string" ? encoder.encode(text) : text;
		chunks.push(bytes);
		position += bytes.length;
	};
	const beginObject = (text: string) => {
		offsets.push(position);
		push(text);
	};

	push("%PDF-1.4\n");
	beginObject("1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n");
	beginObject("2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n");
	beginObject("3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595.28 841.89] /Contents 4 0 R /Resources << /XObject << /Im0 5 0 R >> >> >>\nendobj\n");
	const content = "q\n595.28 0 0 841.89 0 0 cm\n/Im0 Do\nQ\n";
	beginObject(`4 0 obj\n<< /Length ${content.length} >>\nstream\n${content}endstream\nendobj\n`);
	beginObject(`5 0 obj\n<< /Type /XObject /Subtype /Image /Width ${canvas.width} /Height ${canvas.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`);
	push(jpeg);
	push("\nendstream\nendobj\n");

	const xrefStart = position;
	let xref = `xref\n0 ${offsets.length + 1}\n0000000000 65535 f \n`;
	for (const offset of offsets) xref += `${String(offset).padStart(10, "0")} 00000 n \n`;
	xref += `trailer\n<< /Size ${offsets.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF\n`;
	push(xref);

	return new Blob(chunks as BlobPart[], { type: "application/pdf" });
}
